import { Injectable } from '@angular/core';
import { IAppConfig } from '../interfaces/app-config';
import { HttpClient, HttpParams } from '@angular/common/http';
import { AppConfigService } from 'src/app/services/app-config.service';
import { IGetCptCodesPayload } from '../interfaces/get-cpt-codes-payload';
import { catchError, Observable } from 'rxjs';
import { IGetCptCodesResponse } from '../interfaces/cpt-codes-response';
import { IReferenceData } from '../interfaces/form/reference-data.model';
import { handleError } from '../functions/error-handler';
import { IEcnounterMeetingDetails } from '../interfaces/encounter-neeting-details';
import {
  SaveStudentCptCodeRequest,
  SaveStudentCptCodeResponse,
} from '../models/save-student-cpt-code-request';
import {
  IAnnualGoalsAddressed,
  ICptCodesAddressed,
  ISaveEncounterRequestDto,
  IServiceEntryDetails,
  StudentPresentSessionDetails,
} from '../interfaces/service-entry-details';
import { ProviderIdResponse } from '../interfaces/provider-list';
import { MandateAssignmentPayload } from 'src/app/modules/student-profile/interfaces/save-history-assignments-payload';
import { assertTrustedUrl } from '../functions/validate-url';

@Injectable({
  providedIn: 'root',
})
export class EcounterEntryService {
  config: IAppConfig;

  constructor(
    private http: HttpClient,
    private appConfigService: AppConfigService,
  ) {
    this.config = this.appConfigService.getConfig();
  }

  getCPTCodes(
    payload: IGetCptCodesPayload,
  ): Observable<IGetCptCodesResponse[]> {
    return this.http.post<IGetCptCodesResponse[]>(
      `${this.config.apiUrl}/EncounterEntry/GetCPTCodesGroupList`,
      payload,
    );
  }

  public getServiceTypes(providerSk: string): Observable<IReferenceData[]> {
    const params: HttpParams = new HttpParams().set('providerSk', providerSk);
    return this.http
      .get<IReferenceData[]>(
        `${this.config.apiUrl}/Provider/GetProviderServicetypes`,
        {
          params,
        },
      )
      .pipe(catchError(handleError));
  }

  getMeetingDetails(meetingSk: string): Observable<IEcnounterMeetingDetails> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/EncounterEntry/GetMeetingDetails/${encodeURIComponent(meetingSk)}`,
      this.config.apiUrl
    );
    return this.http.get<IEcnounterMeetingDetails>(url);
  }

  saveCptCode(
    payload: SaveStudentCptCodeRequest,
  ): Observable<SaveStudentCptCodeResponse> {
    return this.http.post<SaveStudentCptCodeResponse>(
      `${this.config.apiUrl}/EncounterEntry/SaveGroupCPTCodesForAllStudents`,
      payload,
    );
  }

  getServiceEntryDetails(
    encounterSk: string,
  ): Observable<IServiceEntryDetails> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/EncounterEntry/GetEncounterDetailsById/${encodeURIComponent(encounterSk)}`,
      this.config.apiUrl
    );
    return this.http.get<IServiceEntryDetails>(url);
  }

  getStudentPreviousSessionDetails(
    providerSk: string,
    studentSk: string,
    encounterSk: string,
  ): Observable<StudentPresentSessionDetails[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/EncounterEntry/GetStudentPreviousSessionDetailsById/${encodeURIComponent(providerSk)}/${encodeURIComponent(studentSk)}/${encodeURIComponent(encounterSk)}`,
      this.config.apiUrl
    );
    return this.http.get<StudentPresentSessionDetails[]>(url);
  }

  getAnnualGoalsLookup(mandateSk: string): Observable<IAnnualGoalsAddressed[]> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/EncounterEntry/GetAnualGoalsLookup/${encodeURIComponent(mandateSk)}`,
      this.config.apiUrl
    );
    return this.http.get<IAnnualGoalsAddressed[]>(url);
  }

  getCptCodesGroupList(body: {
    encounterSk: string | null;
    studentSk: string[];
    serviceTypSk: number;
  }): Observable<ICptCodesAddressed[]> {
    return this.http.post<ICptCodesAddressed[]>(
      `${this.config.apiUrl}/EncounterEntry/GetCPTCodesGroupList`,
      body,
    );
  }

  saveEncounterDetails(body: ISaveEncounterRequestDto): Observable<string> {
    return this.http.post<string>(
      `${this.config.apiUrl}/EncounterEntry/SaveEncounterDetails`,
      body,
    );
  }

  submitEncounterStatus(body: {
    encounterSk: string;
    statusId: number;
    comments: string;
  }): Observable<any> {
    return this.http.post(
      `${this.config.apiUrl}/EncounterEntry/SubmitEncounterStatus`,
      body,
    );
  }

  deleteEncounter(encounterSk: string | number): Observable<void> {
    const url: string = assertTrustedUrl(
      `${this.config.apiUrl}/DeleteEncounter/${encodeURIComponent(String(encounterSk))}`,
      this.config.apiUrl
    );
    return this.http.post<void>(url, null);
  }

  getViewSubmissionDetails(encounterSk: string): Observable<void> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/EncounterEntry/ViewSubmissionDetails/${encodeURIComponent(encounterSk)}`,
      this.config.apiUrl
    );
    return this.http.get<any>(url);
  }

  getProviderId(providerSk: string): Observable<string> {
    return this.http.get(
      `${this.config.apiUrl}/Provider/GetProviderId?providerSk=${providerSk}`,
      { responseType: 'text' }
    );
  }

  getUDOProviderdetails(providerSk: string): Observable<any> {
    const url = assertTrustedUrl(
      `${this.config.apiUrl}/EncounterEntry/getUDOProviderdetails/${encodeURIComponent(providerSk)}`,
      this.config.apiUrl
    );
    return this.http.get<any>(url);
  }

  getHistoryMandatedetails(payload:{
  studentId: string,
  pageSize: number,
  pageNumber: number
  } ): Observable<any> {
    return this.http.post<any>(
     `${this.config.apiUrl}/EncounterEntry/GetHistMandatesByStudent`,
      payload
   );
 }
 saveHistoryAssignments(payload: MandateAssignmentPayload): Observable<any> {
  return this.http.post<any>(
   `${this.config.apiUrl}/EncounterEntry/SaveHistAssignments`,
    payload
 );
}
}
